import { FC, JSX, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Section,
  Select,
  Spinner,
  Tabs,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import MarkdownToolbar from "@/features/jobs/components/manage/MarkdownToolbar";
import { Job } from "@/models/Job.model";
import DateObject from "react-date-object";
import { timestamp } from "@/api/firebase/firebase.config";
import { Timestamp } from "firebase/firestore";
import { useEvents } from "@/features/events/hooks/useEvents";
import Alert from "@/components/Alert/Alert";
import { useBrands } from "@/features/brands/hooks/useBrands";

interface JobInputFormProps {
  loading: boolean;
  error: Error | null;
  handleSubmit: (values: Job) => void;
  values: Job;
  setValues: (values: Job) => void;
  buttonText?: string;
}

const JobInputForm: FC<JobInputFormProps> = ({
  loading,
  error,
  handleSubmit,
  values,
  setValues,
  buttonText = "Add Job Listing",
}): JSX.Element => {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [isEventCalendarVisible, setIsEventCalendarVisible] = useState(false);

  const {
    data: events,
    loading: loadingEvents,
    error: errorEvents,
  } = useEvents();

  const {
    data: brands,
    loading: loadingBrands,
    error: errorBrands,
  } = useBrands();

  const setNewRegisterValue = (
    targetName: string,
    value:
      | string
      | boolean
      | { start: Timestamp; end: Timestamp }
      | {
          role: string;
          event: string;
          responsibilities: string;
          qualifications: string;
          offer: string;
        }
      | Timestamp
  ) => {
    const updatedValues = { ...values, [targetName]: value };
    setValues(updatedValues as Job);
  };

  return (
    <Flex direction="column" gap="3">
      {/* Position */}
      <Flex direction="column" gap="1">
        <Text as="label" htmlFor="position" size="2">
          Job Position
        </Text>
        <TextField.Root
          id="position"
          placeholder="F.e. Promoter, Photographer, etc."
          variant="soft"
          color="gray"
          value={values.position}
          onChange={(e) => {
            setNewRegisterValue("position", e.target.value);
          }}
        />
      </Flex>

      {/* Brand */}
      <Flex gap="5">
        <Flex width="50%" direction="column" gap="1">
          <Text as="label" htmlFor="brand" size="2">
            Brand
          </Text>
          {loadingBrands && <Spinner />}
          {errorBrands && <Alert>{errorBrands.message}</Alert>}
          <Select.Root
            defaultValue={brands ? brands[0].slug : ""}
            value={values.brand}
            onValueChange={(value) => setNewRegisterValue("brand", value)}
          >
            <Select.Trigger variant="soft" color="gray" />
            <Select.Content>
              <Select.Group>
                <Select.Label>Brands</Select.Label>
                {brands &&
                  brands.map((brand) => (
                    <Select.Item key={brand.slug} value={brand.slug}>
                      {brand.name}
                    </Select.Item>
                  ))}
              </Select.Group>
            </Select.Content>
          </Select.Root>
        </Flex>

        {/* Event */}
        <Flex width="50%" direction="column" gap="1">
          <Text as="label" htmlFor="event" size="2">
            Event
          </Text>
          {loadingEvents && <Spinner />}
          {errorEvents && <Alert>{errorEvents.message}</Alert>}
          <Select.Root
            defaultValue={events ? events[0].slug : ""}
            value={values.event}
            onValueChange={(value) => setNewRegisterValue("event", value)}
          >
            <Select.Trigger variant="soft" color="gray" />
            <Select.Content>
              <Select.Group>
                <Select.Label>Events</Select.Label>
                {events &&
                  events.map((event) => (
                    <Select.Item key={event.slug} value={event.slug}>
                      {event.name}
                    </Select.Item>
                  ))}
              </Select.Group>
            </Select.Content>
          </Select.Root>
        </Flex>
      </Flex>

      <Flex gap="5">
        <Flex width="50%" direction="column" gap="1" position="relative">
          <Text as="label" size="2">
            Event dates
          </Text>
          <TextField.Root
            type="text"
            variant="soft"
            color="gray"
            readOnly
            onClick={() => setIsEventCalendarVisible(!isEventCalendarVisible)}
            placeholder="Select a date"
            value={
              new DateObject(values.event_dates.start.toDate()).format(
                "DD.MM.YYYY"
              ) +
              " - " +
              new DateObject(values.event_dates.end.toDate()).format(
                "DD.MM.YYYY"
              )
            }
          />
          {isEventCalendarVisible && (
            <Box
              position="absolute"
              inset="100% auto auto 0"
              style={{ zIndex: 1001 }}
            >
              <Calendar
                defaultView="month"
                onChange={(value) => {
                  if (Array.isArray(value) && value.length === 2) {
                    setIsEventCalendarVisible(false);
                    setNewRegisterValue("event_dates", {
                      start: value[0]
                        ? timestamp.fromDate(value[0])
                        : timestamp.now(),
                      end: value[1]
                        ? timestamp.fromDate(value[1])
                        : timestamp.now(),
                    });
                  }
                }}
                selectRange={true}
                returnValue="range"
              />
            </Box>
          )}
        </Flex>

        <Flex width="50%" direction="column" gap="1" position="relative">
          <Text as="label" size="2">
            Application Deadline
          </Text>
          <TextField.Root
            type="text"
            variant="soft"
            color="gray"
            readOnly
            onClick={() => setIsCalendarVisible(!isCalendarVisible)}
            placeholder="Select a date"
            value={new DateObject(values.application_deadline.toDate()).format(
              "DD.MM.YYYY"
            )}
          />
          {isCalendarVisible && (
            <Box
              position="absolute"
              inset="100% auto auto 0"
              style={{ zIndex: 1001 }}
            >
              <Calendar
                defaultView="month"
                onChange={(e) => {
                  setIsCalendarVisible(false);
                  setNewRegisterValue(
                    "application_deadline",
                    timestamp.fromDate(e as Date)
                  );
                }}
              />
            </Box>
          )}
        </Flex>
      </Flex>

      {/* Compensation */}
      <Flex direction="column" gap="1">
        <Text as="label" htmlFor="compensation" size="2">
          Compensation
        </Text>
        <TextField.Root
          id="compensation"
          placeholder="F.e. hourly rate, salary, etc."
          variant="soft"
          color="gray"
          value={values.compensation}
          onChange={(e) => {
            setNewRegisterValue("compensation", e.target.value);
          }}
        />
      </Flex>

      <Section size="1">
        <Heading size="4" mb="0" as="h2">
          Descriptions
        </Heading>
        <Tabs.Root defaultValue="role">
          <Tabs.List size="1">
            <Tabs.Trigger value="role">Role</Tabs.Trigger>
            <Tabs.Trigger value="event">Event</Tabs.Trigger>
            <Tabs.Trigger value="responsiblities">Responsiblities</Tabs.Trigger>
            <Tabs.Trigger value="qualifications">Qualifications</Tabs.Trigger>
            <Tabs.Trigger value="offer">Offer</Tabs.Trigger>
          </Tabs.List>

          <Box pt="3">
            <Tabs.Content value="role">
              {/* Role Description */}
              <Flex direction="column" gap="1">
                <Text as="label" htmlFor="role-description" size="2">
                  Role Description
                </Text>
                <Box position="relative">
                  <TextArea
                    color="gray"
                    spellCheck={false}
                    id="role-description"
                    variant="soft"
                    rows={5}
                    style={{ paddingTop: 48 }}
                    value={values.descriptions.role}
                    onChange={(e) => {
                      setNewRegisterValue("descriptions", {
                        ...values.descriptions,
                        role: e.target.value,
                      });
                    }}
                  />
                  <Box position="absolute" m="2" top="0" left="0" right="0">
                    <MarkdownToolbar id="role-description" />
                  </Box>
                </Box>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="event">
              {/* Event Description */}
              <Flex direction="column" gap="1">
                <Text as="label" htmlFor="event-description" size="2">
                  About the Event
                </Text>
                <Box position="relative">
                  <TextArea
                    color="gray"
                    spellCheck={false}
                    id="event-description"
                    variant="soft"
                    rows={5}
                    style={{ paddingTop: 48 }}
                    value={values.descriptions.event}
                    onChange={(e) => {
                      setNewRegisterValue("descriptions", {
                        ...values.descriptions,
                        event: e.target.value,
                      });
                    }}
                  />
                  <Box position="absolute" m="2" top="0" left="0" right="0">
                    <MarkdownToolbar id="event-description" />
                  </Box>
                </Box>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="responsiblities">
              {/* Responsibilities */}
              <Flex direction="column" gap="1">
                <Text as="label" htmlFor="responsibilities" size="2">
                  Responsibilities
                </Text>
                <Box position="relative">
                  <TextArea
                    color="gray"
                    spellCheck={false}
                    id="responsibilities"
                    variant="soft"
                    rows={5}
                    style={{ paddingTop: 48 }}
                    value={values.descriptions.responsibilities}
                    onChange={(e) => {
                      setNewRegisterValue("descriptions", {
                        ...values.descriptions,
                        responsibilities: e.target.value,
                      });
                    }}
                  />
                  <Box position="absolute" m="2" top="0" left="0" right="0">
                    <MarkdownToolbar id="responsibilities" />
                  </Box>
                </Box>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="qualifications">
              {/* Qualifications */}
              <Flex direction="column" gap="1">
                <Text as="label" htmlFor="qualifications" size="2">
                  Qualifications & Requirements
                </Text>
                <Box position="relative">
                  <TextArea
                    color="gray"
                    spellCheck={false}
                    id="qualifications"
                    variant="soft"
                    rows={5}
                    style={{ paddingTop: 48 }}
                    value={values.descriptions.qualifications}
                    onChange={(e) => {
                      setNewRegisterValue("descriptions", {
                        ...values.descriptions,
                        qualifications: e.target.value,
                      });
                    }}
                  />
                  <Box position="absolute" m="2" top="0" left="0" right="0">
                    <MarkdownToolbar id="qualifications" />
                  </Box>
                </Box>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="offer">
              {/* What we Offer */}
              <Flex direction="column" gap="1">
                <Text as="label" htmlFor="offer" size="2">
                  What We Offer
                </Text>
                <Box position="relative">
                  <TextArea
                    color="gray"
                    spellCheck={false}
                    id="offer"
                    variant="soft"
                    rows={5}
                    style={{ paddingTop: 48 }}
                    value={values.descriptions.offer}
                    onChange={(e) => {
                      setNewRegisterValue("descriptions", {
                        ...values.descriptions,
                        offer: e.target.value,
                      });
                    }}
                  />
                  <Box position="absolute" m="2" top="0" left="0" right="0">
                    <MarkdownToolbar id="offer" />
                  </Box>
                </Box>
              </Flex>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Section>

      {error && error.message && <Box>{error.message}</Box>}
      <Flex direction="row" gap="1">
        <Button
          onClick={() => {
            console.log("values", values);
            console.log(
              "start",
              new DateObject(values.event_dates.start.toDate()).format(
                "DD.MM.YYYY"
              )
            );
            console.log(
              "end",
              new DateObject(values.event_dates.end.toDate()).format(
                "DD.MM.YYYY"
              )
            );
            console.log(
              "application_deadline",
              new DateObject(values.application_deadline.toDate()).format(
                "DD.MM.YYYY"
              )
            );
            if (!loading) {
              handleSubmit(values);
            }
          }}
        >
          {loading ? <Spinner /> : buttonText}
        </Button>
      </Flex>
    </Flex>
  );
};

export default JobInputForm;
